import axios from 'axios';

// Public portal API - no authentication required
const portalApi = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4000/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// ============ Types ============

export interface PortalReviewData {
    id: string;
    title: string;
    description?: string;
    previewUrl?: string;
    previewType?: string;
    attachments: string[];
    brandContact: {
        name: string;
        email: string;
        company: string;
    };
    submittedAt: string;
    submittedBy: {
        name: string;
        email: string;
    };
    versionNumber: number;
    currentRound: number;
    reviewDueAt?: string;
    status: string;
    previousFeedback: Array<{
        decision: string;
        overallComments?: string;
        submittedAt: string;
    }>;
    changeRequests: Array<{
        requestId: string;
        title: string;
        description: string;
        priority: string;
        category: string;
        status: string;
    }>;
}

export interface TimestampNote {
    timestamp: string;
    note: string;
    priority: 'low' | 'medium' | 'high';
}

export interface SubmitFeedbackData {
    decision: 'approved' | 'approved_with_changes' | 'rejected' | 'needs_discussion';
    overallComments?: string;
    timestampFeedback?: TimestampNote[];
    changeRequests?: Array<{
        title: string;
        description: string;
        priority: 'low' | 'medium' | 'high' | 'critical';
        category: 'content' | 'timing' | 'visual' | 'audio' | 'branding' | 'compliance' | 'other';
        timestampRef?: string;
    }>;
}

export interface PortalStatusData {
    status: string;
    versionNumber: number;
    currentRound: number;
    lastUpdated: string;
}

// ============ API Service ============

export const portalService = {
    /**
     * Get review details by portal token
     */
    getReview: async (token: string): Promise<PortalReviewData> => {
        const response = await portalApi.get(`/portal/review/${token}`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    /**
     * Submit feedback for a review
     */
    submitFeedback: async (token: string, data: SubmitFeedbackData): Promise<void> => {
        await portalApi.post(`/portal/review/${token}/feedback`, data);
    },

    /**
     * Get current status of a review
     */
    getStatus: async (token: string): Promise<PortalStatusData> => {
        const response = await portalApi.get(`/portal/review/${token}/status`);
        return response.data;
    },
};

export default portalService;
